export const PAGE_VISITS_STORAGE_KEY = 'erp-page-visits'
export const MAX_STORED_PAGE_VISITS = 40

export type PageVisitScope = { tenant: string; userId: string | number }

export function pageVisitsStorageKey(scope: PageVisitScope): string {
  return `${PAGE_VISITS_STORAGE_KEY}:${encodeURIComponent(scope.tenant)}:${encodeURIComponent(String(scope.userId))}`
}

/** Returns null until both identity dimensions are known; callers must not fall back to shared history. */
export function resolvePageVisitScope(userId?: string | number | null): PageVisitScope | null {
  if (userId == null || typeof localStorage === 'undefined') return null
  let tenant = localStorage.getItem('tenant_slug')?.trim() ?? ''
  if (!tenant) {
    try {
      const raw = localStorage.getItem('authUser')
      const parsed = raw ? JSON.parse(raw) as { tenant_slug?: string } : null
      tenant = parsed?.tenant_slug?.trim() ?? ''
    } catch {
      tenant = ''
    }
  }
  return tenant ? { tenant, userId } : null
}

const IGNORED_LINKS = new Set([
  '/',
  '/login',
  '/logout',
  '/register',
  '/forgot-password',
  '/reset-password',
])

export type PageVisitRecord = {
  count: number
  lastVisitedAt: number
}

export type PageVisitMap = Record<string, PageVisitRecord>

let lastRecord: { link: string; storageKey: string; at: number } | null = null

function canUseStorage(): boolean {
  return typeof localStorage !== 'undefined'
}

export function readPageVisits(scope?: PageVisitScope | null): PageVisitMap {
  try {
    if (!canUseStorage()) return {}
    if (scope === null) return {}
    const raw = localStorage.getItem(scope ? pageVisitsStorageKey(scope) : PAGE_VISITS_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as PageVisitMap
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed
  } catch {
    return {}
  }
}

function writePageVisits(visits: PageVisitMap, scope?: PageVisitScope | null) {
  try {
    if (!canUseStorage()) return
    if (scope === null) return
    localStorage.setItem(scope ? pageVisitsStorageKey(scope) : PAGE_VISITS_STORAGE_KEY, JSON.stringify(visits))
  } catch {
    /* private mode */
  }
}

export function isIgnoredVisitLink(link: string): boolean {
  const path = link.split('?')[0] || link
  return IGNORED_LINKS.has(path)
}

function pruneVisits(visits: PageVisitMap, keepLink: string): PageVisitMap {
  const keep = visits[keepLink]
  const rest = Object.entries(visits).filter(([link]) => link !== keepLink)
  if (rest.length + (keep ? 1 : 0) <= MAX_STORED_PAGE_VISITS) return visits

  rest.sort((a, b) => {
    if (b[1].count !== a[1].count) return a[1].count - b[1].count
    return a[1].lastVisitedAt - b[1].lastVisitedAt
  })
  const kept = rest.slice(-(MAX_STORED_PAGE_VISITS - (keep ? 1 : 0)))
  const next = Object.fromEntries(kept)
  if (keep) next[keepLink] = keep
  return next
}

export function incrementPageVisit(
  link: string,
  now = Date.now(),
  scope?: PageVisitScope | null,
): PageVisitMap {
  const normalized = link.trim()
  if (!normalized || isIgnoredVisitLink(normalized)) return readPageVisits(scope)
  if (scope === null) return {}

  const storageKey = scope ? pageVisitsStorageKey(scope) : PAGE_VISITS_STORAGE_KEY
  if (lastRecord?.link === normalized && lastRecord.storageKey === storageKey) return readPageVisits(scope)

  lastRecord = { link: normalized, storageKey, at: now }
  const visits = readPageVisits(scope)
  const current = visits[normalized]
  visits[normalized] = {
    count: (current?.count ?? 0) + 1,
    lastVisitedAt: now,
  }
  const next = pruneVisits(visits, normalized)
  writePageVisits(next, scope)
  return next
}

export function rankedVisitLinks(visits: PageVisitMap = readPageVisits()): string[] {
  return Object.entries(visits)
    .filter(([link]) => !isIgnoredVisitLink(link))
    .sort((a, b) => {
      if (b[1].count !== a[1].count) return b[1].count - a[1].count
      return b[1].lastVisitedAt - a[1].lastVisitedAt
    })
    .map(([link]) => link)
}

export function resetPageVisitRecorder() {
  lastRecord = null
}
